import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Boxes, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ConnectedAreas from "../../components/os/ConnectedAreas";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const STATUSES = [["pending", "Pendiente"], ["in_production", "En producción"], ["shipped", "Enviada"], ["received", "Recibida"]];
const STATUS_CLS = { pending: "bg-navy/10 text-navy/60", in_production: "bg-violet/10 text-violet", shipped: "bg-gold/15 text-gold", received: "bg-emerald-500/10 text-emerald-600" };
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";

export default function VettaProject() {
  const { gid } = useParams();
  const [group, setGroup] = useState(null);
  const [orders, setOrders] = useState([]);
  const [f, setF] = useState({ product_name: "", qty: 1, supplier: "", due_date: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/group/${gid}`).then((r) => setGroup(r.data)).catch(() => {});
    api.get(`/projects/${gid}/vetta/orders`).then((r) => setOrders(r.data)).catch(() => {});
  }, [gid]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.product_name.trim()) return;
    setBusy(true);
    try { await api.post(`/projects/${gid}/vetta/orders`, { ...f, qty: Number(f.qty) || 1 }); setF({ product_name: "", qty: 1, supplier: "", due_date: "" }); toast.success("Orden creada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };
  const setStatus = async (o, status) => { await api.patch(`/projects/${gid}/vetta/orders/${o.id}`, { status }); load(); };
  const del = async (o) => { await api.delete(`/projects/${gid}/vetta/orders/${o.id}`); load(); };

  return (
    <OSLayout title={group ? `VETTA · ${group.name}` : "VETTA Factory"}>
      <div className="space-y-6">
        <ConnectedAreas module="vetta" entityId={gid} />

        <div className="flex items-center gap-2 text-navy/70">
          <Boxes className="w-5 h-5" /><h2 className="text-xl font-medium">Órdenes de fabricación del proyecto</h2>
        </div>
        <p className="text-sm text-navy/50 -mt-3">Genera aquí las órdenes de producción que este proyecto necesita.</p>

        <div className="bg-white border border-navy/10 rounded-lg p-5 grid md:grid-cols-5 gap-2 items-end" data-testid="vetta-project-form">
          <input data-testid="vp-product" placeholder="Producto / pieza" value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} className={`${input} md:col-span-2`} />
          <input data-testid="vp-qty" type="number" min="1" placeholder="Cantidad" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} className={input} />
          <input data-testid="vp-supplier" placeholder="Proveedor" value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} className={input} />
          <div className="flex gap-2">
            <input data-testid="vp-due" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={input} />
            <button data-testid="vp-add" onClick={add} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-gradient-violet text-white text-sm px-4 hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="text-navy/40 py-10 text-center border border-dashed border-navy/15 rounded-lg" data-testid="vetta-project-empty">Aún no hay órdenes para este proyecto.</div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <motion.div key={o.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}
                data-testid={`vp-order-${o.id}`} className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{o.product_name} <span className="text-navy/40 font-normal">×{o.qty}</span></div>
                  <div className="text-[11px] text-navy/45 truncate">{o.supplier || "Sin proveedor"}{o.due_date ? ` · entrega ${o.due_date}` : ""}</div>
                </div>
                <select data-testid={`vp-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o, e.target.value)} className={`text-[11px] px-2 py-1 rounded-full outline-none ${STATUS_CLS[o.status] || "bg-navy/10"}`}>
                  {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button data-testid={`vp-del-${o.id}`} onClick={() => del(o)} className="text-navy/30 hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </OSLayout>
  );
}
